/* eslint-disable import/no-anonymous-default-export */
// © Microsoft Corporation. All rights reserved.
import React, { useEffect, useState } from 'react';
import { Label, Overlay, Stack } from '@fluentui/react';
import Header from '../containers/Header';
import MediaGallery from '../containers/MediaGallery';
import MediaFullScreen from './MediaFullScreen';
import CommandPanel, { CommandPanelTypes } from './CommandPanel';
import { Constants, labelConstants, URLOptionsConstants } from '../core/constants';
import {
    headerStyles,
    containerStyles,
    paneStyles,
    hiddenContainerClassName,
    activeContainerClassName,
    activeContainer60ClassName,
    activeContainer40ClassName,
    activeContainerScreenShareClassName,
    activeContainerLocalVideoClassName,
    activeContainerLocalVideoVerticalClassName,
    activeContainerLocalVideoVertical66ClassName,
    activeContainerScreenShareVerticalClassName,
    activeContainerScreenShareVertical34ClassName,
    containerDarkStyles,
    //loadingStyle,
    overlayStyles,
    footerStyle,
    labelFooterStyle
} from './styles/GroupCall.styles';
import {
    Call,
    AudioDeviceInfo,
    VideoDeviceInfo,
    RemoteParticipant,
    CallAgent,
    DeviceManager,
    LocalVideoStream
} from '@azure/communication-calling';
import { ParticipantStream } from 'core/reducers/index.js';
import { pingAndReconnectConnectedUser, DisableAutoGain} from '../core/sideEffects';
import { IsCandidateMode, IsHiddenUser, IsCentralScreenMode, IsSecondaryStream, IsObserverMode} from 'Utils/Utils';

export interface GroupCallProps {
    userId: string;
    groupId: string;
    call: Call;
    callAgent: CallAgent;
    deviceManager: DeviceManager;
    mic: boolean;
    remoteParticipants: RemoteParticipant[];
    callState: string;
    localVideo: boolean;
    screenShareStreams: ParticipantStream[];
    audioDeviceInfo: AudioDeviceInfo;
    videoDeviceInfo: VideoDeviceInfo;
    audioDeviceList: AudioDeviceInfo[];
    videoDeviceList: VideoDeviceInfo[];
    screenWidth: number;
    shareScreen: boolean;
    setAudioDeviceInfo(deviceInfo: AudioDeviceInfo): void;
    setVideoDeviceInfo(deviceInfo: VideoDeviceInfo): void;
    mute(): void;
    isGroup(): void;
    joinGroup(): void;
    endCallHandler(): void;
    localVideoStream: LocalVideoStream;
    useInterval(callback: any, delay: number): void;
    setLocalVideoStream(device: LocalVideoStream | undefined): void;
}

export default (props: GroupCallProps): JSX.Element => {
    const [selectedPane, setSelectedPane] = useState(CommandPanelTypes.None);
    const activeScreenShare = props.screenShareStreams && props.screenShareStreams.length === 1;
    const activeParticipants = props.remoteParticipants.filter((p) => !IsHiddenUser(p.displayName + ""));
    const showFooter = activeParticipants.length === 0;
    const isHorizontal = window.innerWidth >= (2 * window.innerHeight);

    const urlParams = new URLSearchParams(window.location.search.substring(1));
    const threadId = urlParams.get(URLOptionsConstants.THREADID);
    const [localPreview, setLocalPreview] = useState(true);

    useEffect(() => {
        let listener: NodeJS.Timeout = setInterval(() => {
            if (IsCandidateMode() && threadId != null) {
                pingAndReconnectConnectedUser(props.userId, threadId, props.endCallHandler);
            }
        }, Constants.PING_USER_IN_SECONDS * 1000);
        return () => {
            clearInterval(listener);
        };
    }, [props.userId, IsCandidateMode]);

    useEffect(() => {
        if (IsCentralScreenMode())
        {
            var audioList = document.getElementsByTagName('audio');
            if (audioList) {
                for (let i = 0; i < audioList.length; i++) {
                    audioList[i].pause();
                }
            }
        }
    }, [IsCentralScreenMode, props.callState, props.remoteParticipants]);

    useEffect(() => {
        DisableAutoGain();
    });

    useEffect(() => {
        let listener: NodeJS.Timeout = setInterval(() => {
            if (IsSecondaryStream() && threadId != null) {
                var audioList = document.getElementsByTagName('audio');
                if (audioList) {
                    for (let i = 0; i < audioList.length; i++) {
                        audioList[i].pause();
                    }
                }
            }
        }, Constants.PING_USER_IN_SECONDS * 1000);
        return () => {
            clearInterval(listener);
        };
    }, [props.userId, IsSecondaryStream]);


    useEffect(() => {
        if (IsObserverMode()) {
            setTimeout(function () {
                props.endCallHandler()
            }, Constants.OBSERVER_CONNECTION_MS);   
        }
    }, [IsObserverMode]);



    const GetCentralScreenCameraStyle = () => {
        if (activeScreenShare)
        {
            if (isHorizontal)
                return activeContainerLocalVideoClassName;
            else if (activeParticipants.length > 1)
                return activeContainerLocalVideoVertical66ClassName;
            else
                return activeContainerLocalVideoVerticalClassName;
        }
        else
            return activeContainerClassName;
    }

    const GetCentralScreenShareStyle = () => {
        if (activeScreenShare) {
            if (isHorizontal)
                return activeContainerScreenShareClassName;
            else if (activeParticipants.length > 1)
                return activeContainerScreenShareVertical34ClassName;
            else
                return activeContainerScreenShareVerticalClassName;
        }
        else
            return hiddenContainerClassName;
    }
    return (
        <Stack horizontalAlign="center" verticalAlign="center" styles={containerStyles}>
            <Stack.Item styles={headerStyles}>
                <Header
                    selectedPane={selectedPane}
                    setSelectedPane={setSelectedPane}
                    endCallHandler={(): void => {
                        props.endCallHandler();
                    }}
                    screenWidth={props.screenWidth}
                    localVideoStream={props.localVideoStream}
                    setLocalVideoStream={props.setLocalVideoStream}
                    localPreview={localPreview}
                    setLocalPreview={setLocalPreview}
                />
            </Stack.Item>
            <Stack.Item styles={containerStyles}>
                {!props.shareScreen ? (
                    props.callState === Constants.CONNECTED && (
                        <Stack horizontal styles={containerStyles}>
                            <Stack.Item styles={containerStyles}>
                                {IsCentralScreenMode() ?
                                    <Stack horizontal={isHorizontal} styles={containerDarkStyles}>
                                        <Stack.Item grow styles={GetCentralScreenCameraStyle()}>
                                            <MediaGallery localVideoStream={props.localVideoStream} localPreview={localPreview} />
                                        </Stack.Item>
                                        <Stack.Item grow styles={GetCentralScreenShareStyle()}>
                                            {activeScreenShare && <MediaFullScreen activeScreenShareStream={props.screenShareStreams[0]} />}
                                        </Stack.Item>
                                    </Stack>
                                    :
                                    <Stack horizontal={window.innerWidth > Constants.MINI_HEADER_WINDOW_WIDTH} styles={containerStyles}>
                                        <Stack.Item grow styles={activeScreenShare ? (window.innerWidth > Constants.MINI_HEADER_WINDOW_WIDTH ? activeContainerClassName : activeContainer40ClassName) : hiddenContainerClassName}>
                                            {activeScreenShare && <MediaFullScreen activeScreenShareStream={props.screenShareStreams[0]} />}
                                        </Stack.Item>
                                        <Stack.Item grow styles={activeScreenShare ? (window.innerWidth > Constants.MINI_HEADER_WINDOW_WIDTH ? activeContainerClassName : activeContainer60ClassName) : activeContainerClassName}>
                                            <MediaGallery localVideoStream={props.localVideoStream} localPreview={localPreview} />
                                        </Stack.Item>
                                    </Stack>
                                }
                            </Stack.Item>
                            {selectedPane !== CommandPanelTypes.None &&
                                (window.innerWidth > Constants.MINI_HEADER_WINDOW_WIDTH ? (
                                    <Stack.Item disableShrink styles={paneStyles}>
                                        <CommandPanel {...props} selectedPane={selectedPane} setSelectedPane={setSelectedPane} />
                                    </Stack.Item>
                                ) : (

                                    <Overlay styles={overlayStyles}>
                                        <CommandPanel {...props} selectedPane={selectedPane} setSelectedPane={setSelectedPane} />
                                    </Overlay>
                                ))}
                        </Stack>
                    )
                ) : (
                    <Stack horizontal styles={containerStyles}>
                        <Stack.Item grow styles={activeContainerClassName}>
                            <MediaGallery localVideoStream={props.localVideoStream} localPreview={localPreview} />
                        </Stack.Item>
                        {selectedPane !== CommandPanelTypes.None &&
                            (window.innerWidth > Constants.MINI_HEADER_WINDOW_WIDTH ? (
                                <Stack.Item disableShrink styles={paneStyles}>
                                    <CommandPanel {...props} selectedPane={selectedPane} setSelectedPane={setSelectedPane} />
                                </Stack.Item>
                            ) : (

                                <Overlay styles={overlayStyles}>
                                    <CommandPanel {...props} selectedPane={selectedPane} setSelectedPane={setSelectedPane} />
                                </Overlay>
                            ))}
                    </Stack>
                )}
            </Stack.Item>
            {showFooter && !IsCandidateMode() && !IsSecondaryStream() ?(
                <Stack.Item styles={footerStyle}>
                    <div>
                        <Label className={labelFooterStyle}>{labelConstants.WaitForOthers}</Label>
                    </div>
                </Stack.Item>
            ) : (
                <div>
                </div>
            )}
        </Stack>
    );
};